import { fieldLength, fieldRequired, type PlatformContract } from "@ondit/publishing";
import { Button, Checkbox, Input, Label, NativeSelect, Textarea } from "@ondit/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { useAccess } from "~/core/access";
import { ApiError, post } from "~/core/api";
import { pick, useI18n } from "~/core/i18n";
import { PlatformPreview } from "~/modules/analytics/features/platform-publishing/preview";
import { type ContentItem, fileUrl, type StudioFile, studioKey, type Variant } from "./api";
import { issueLabel } from "./issues";
export function VariantEditor({
	brandId,
	item,
	variant,
	platform,
	files,
	onDirty,
}: {
	onDirty: (dirty: boolean) => void;
	brandId: string;
	item: ContentItem;
	variant: Variant;
	platform: PlatformContract;
	files: Map<string, StudioFile>;
}) {
	const { locale } = useI18n();
	const tr = (en: string, zh: string) => pick(locale, [en, zh]);
	const { editor } = useAccess();
	const cache = useQueryClient();
	const [fields, setFields] = useState(variant.document.fields);
	const [selected, setSelected] = useState(
		variant.document.media.filter((a) => ["image", "video"].includes(a.role)).map((a) => a.file_id),
	);
	const [target, setTarget] = useState(variant.document.target_url ?? platform.entry_url);
	const [reviewed, setReviewed] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	function changeFields(value: Record<string, string>) {
		setFields(value);
		onDirty(true);
	}
	function changeSelected(value: string[]) {
		setSelected(value);
		onDirty(true);
	}
	function changeTarget(value: string) {
		setTarget(value);
		onDirty(true);
	}
	function changeReviewed(value: boolean) {
		setReviewed(value);
		onDirty(true);
	}
	const assets = selected.flatMap((id) => {
		const f = files.get(id);
		return f
			? [
					{
						url: fileUrl(id),
						alt: f.name,
						kind: f.content_type.startsWith("video/") ? ("video" as const) : ("image" as const),
					},
				]
			: [];
	});
	const ready = platform.preview.fields.every((f) => {
		const v = fields[f.key] ?? "";
		return (!fieldRequired(f, fields) || !!v.trim()) && (!f.max_length || fieldLength(f, v) <= f.max_length);
	});
	async function save(approve: boolean) {
		setBusy(true);
		setError("");
		try {
			await post(`/entities/${brandId}/studio-variants/${variant.id}`, {
				expected_revision: variant.revision,
				fields,
				file_ids: selected,
				target_url: target,
				approve,
				reviewed,
			});
			onDirty(false);
			await cache.invalidateQueries({ queryKey: studioKey(brandId) });
		} catch (e) {
			setError(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}
	return (
		<div className="studio-form">
			{variant.issues.length > 0 && (
				<div role="status">
					<strong>{tr("Adaptation differences to review", "需要检查的适配差异")}</strong>
					<ul>
						{variant.issues.map((issue) => (
							<li key={issue}>{issueLabel(issue, locale)}</li>
						))}
					</ul>
				</div>
			)}
			<fieldset className="studio-form bare-fieldset" disabled={!editor || busy || !!variant.approved_at}>
				{platform.preview.fields.map((f) => (
					<div className="studio-form" key={f.key}>
						<Label htmlFor={`variant-${f.key}`}>
							{pick(locale, f.label)}{" "}
							{f.max_length && (
								<span className="hint">
									{fieldLength(f, fields[f.key] ?? "")} / {f.max_length}
								</span>
							)}
						</Label>
						{f.kind === "select" ? (
							<NativeSelect
								id={`variant-${f.key}`}
								value={fields[f.key] ?? ""}
								onChange={(e) => changeFields({ ...fields, [f.key]: e.target.value })}
							>
								<option value="" />
								{f.options?.map((o) => (
									<option key={o} value={o}>
										{pick(locale, f.option_labels?.[o]) ?? o}
									</option>
								))}
							</NativeSelect>
						) : f.kind === "textarea" ? (
							<Textarea
								id={`variant-${f.key}`}
								rows={6}
								value={fields[f.key] ?? ""}
								onChange={(e) => changeFields({ ...fields, [f.key]: e.target.value })}
							/>
						) : (
							<Input
								id={`variant-${f.key}`}
								value={fields[f.key] ?? ""}
								onChange={(e) => changeFields({ ...fields, [f.key]: e.target.value })}
							/>
						)}
					</div>
				))}
				<Label>{tr("Included media", "使用的素材")}</Label>
				{[...files.values()]
					.filter((f) => /^(image|video)\//.test(f.content_type))
					.map((f) => (
						<label className="studio-toolbar" key={f.id} htmlFor={`include-${f.id}`}>
							<Checkbox
								id={`include-${f.id}`}
								checked={selected.includes(f.id)}
								onCheckedChange={(on) =>
									changeSelected(on ? [...selected, f.id] : selected.filter((id) => id !== f.id))
								}
							/>
							{f.name} · {f.width} × {f.height} · {Math.ceil(f.size / 1024)} KB
							{f.duration_ms !== null && ` · ${(f.duration_ms / 1000).toFixed(1)} s`}
						</label>
					))}
				<Label htmlFor="studio-target">{tr("Destination editor URL", "目标平台编辑地址")}</Label>
				<Input id="studio-target" value={target} onChange={(e) => changeTarget(e.target.value)} />
				<Button onClick={() => void save(false)}>{tr("Save platform version", "保存平台版本")}</Button>
				<label className="studio-toolbar" htmlFor="studio-reviewed">
					<Checkbox id="studio-reviewed" checked={reviewed} onCheckedChange={(v) => changeReviewed(v === true)} />
					{tr(
						"I reviewed the text, sources, media specifications and platform layout. Selected media and inline images will be accessible by public links for delivery.",
						"我已检查文字、来源、素材规格和平台版式。选中的素材及正文插图将通过公开链接供发布平台读取。",
					)}
				</label>
				<Button variant="primary" disabled={!ready || !reviewed} onClick={() => void save(true)}>
					{tr("Approve and prepare publishing task", "确认版本并准备发布任务")}
				</Button>
			</fieldset>
			{error && <p role="alert">{error}</p>}
			{variant.task_id && (
				<Link
					to={`/w/${brandId}/publish/distribution?tab=prepare&platform=${variant.platform}&task=${variant.task_id}`}
				>
					{tr("Open publishing task", "打开发布任务")}
				</Link>
			)}
			<PlatformPreview
				platform={platform}
				resolvedMedia={assets}
				resolvedFiles={Object.fromEntries([...files.keys()].map((id) => [id, fileUrl(id)]))}
				payload={{
					version: 1,
					platform: platform.id,
					fields,
					media: [],
					source: { page_id: "", content_id: item.id, revision: item.revision, canonical: "" },
					target_url: target,
				}}
			/>
		</div>
	);
}
